import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Inbox as InboxIcon, Lock, Shield, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getDomainFavicon = (url) => {
  if (!url) return null;
  try {
    const domain = new URL(url.startsWith('http') ? url : `https://${url}`).hostname;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
  } catch (e) { return null; }
};

export default function InboxPage({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchInbox = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/inbox`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data);
    } catch (err) { toast.error('Failed to load inbox'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchInbox(); }, [fetchInbox]);

  const handleClick = (item) => {
    if (item.type === 'credential') navigate(`/dashboard/credential/${item.id}`);
    else navigate(`/dashboard/certificates/${item.id}`);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-text-primary font-manrope text-xl">Loading inbox...</div></div>;
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <InboxIcon className="w-8 h-8 text-primary" />
          <h1 className="font-manrope font-bold text-4xl text-text-primary tracking-tight">Inbox</h1>
        </div>
        <p className="text-text-secondary">Items shared with you by your team</p>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-20">
          <InboxIcon className="w-16 h-16 text-text-muted mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="font-manrope font-semibold text-xl text-text-primary mb-2">Inbox empty</h3>
          <p className="text-text-secondary">No one has shared anything with you yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const favicon = getDomainFavicon(item.url);
            return (
              <div
                key={`${item.type}-${item.id}`}
                onClick={() => handleClick(item)}
                className="bg-white border-2 border-gray-200 rounded-xl p-5 flex items-center gap-4 cursor-pointer hover:border-primary/50 hover:shadow-md transition-all group"
                data-testid={`inbox-item-${item.id}`}
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {favicon ? (
                    <img src={favicon} alt="" className="w-5 h-5" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
                  ) : null}
                  <span style={{ display: favicon ? 'none' : 'block' }}>
                    {item.type === 'credential' ? <Lock className="w-5 h-5 text-primary" /> : <Shield className="w-5 h-5 text-primary" />}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-text-primary truncate">{item.title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.type === 'credential' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}`}>
                      {item.type}
                    </span>
                  </div>
                  <p className="text-text-muted text-sm mt-0.5">
                    Shared by <span className="font-medium text-text-secondary">{item.shared_by_name}</span>
                    {item.shared_by_email && <span className="text-text-muted"> ({item.shared_by_email})</span>}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-text-muted">{new Date(item.updated_at).toLocaleDateString()}</p>
                </div>
                <ArrowRight size={16} className="text-text-muted group-hover:text-primary transition-colors flex-shrink-0" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
